import React, {useEffect} from 'react'
import {Row, Col, InlineLoader, SuccessAlert} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {PublicRegisteredCompaniesRetrievalStatus} from '@modifi/enums'

import DynamicEntriesTable, {DynamicEntry} from '../../../../component/dynamic-entries-table'
import SummaryPageFooter from '../../components/summary-page-footer'
import {SECTION_ULTIMATE_BENEFICIAL_OWNERS} from '../../../../constants/sections'
import {FormContentWrapper} from '../../../../ui/form'
import {StyledImg, ImageContainer} from '../../../summary-page-image/styles'
import {StyledParagraph} from '../../../../ui/styles'
import DataSourceWizard from '../../../intro/data-source-wizard'
import SummaryPageHeader from '../../components/summary-page-header'

const tableHeadlines = ['name', 'votingSharePercentage']

/** One row of the BeneficialOwnerSummaryPage table. */
type BeneficialOwnerSummaryEntry = DynamicEntry & {
  route: string
  name: string
  votingSharePercentage?: string
}

export type BeneficialOwnerSummaryPageProps = {
  isLoading: boolean
  uboSummary: BeneficialOwnerSummaryEntry[]
  isReadonly: boolean
  createUbo: () => void
  pageIsVerified: boolean
  hasUbosFromProvider?: boolean
  fetchResolvedCompanyDetails: () => void
  retrievalStatus?: string
  fetchPublicRegisteredCompanies: () => void
}

const BeneficialOwnerSummaryPage: React.FC<BeneficialOwnerSummaryPageProps> = ({
  isLoading,
  uboSummary,
  isReadonly,
  createUbo,
  pageIsVerified,
  hasUbosFromProvider = false,
  fetchResolvedCompanyDetails,
  retrievalStatus = PublicRegisteredCompaniesRetrievalStatus.PENDING_RESOLUTION,
  fetchPublicRegisteredCompanies,
}) => {
  const {t} = useTranslation('charon')

  const UboCount = uboSummary.length

  useEffect(() => {
    fetchPublicRegisteredCompanies()
  }, [fetchPublicRegisteredCompanies])

  useEffect(() => {
    if (
      !isLoading &&
      UboCount < 1 &&
      retrievalStatus === PublicRegisteredCompaniesRetrievalStatus.COMPLETED
    ) {
      fetchResolvedCompanyDetails()
    }
  }, [fetchResolvedCompanyDetails, UboCount, isLoading, retrievalStatus])

  let content
  let showAddUboButton = false
  if (isLoading) {
    content = (
      <Row>
        <Col>
          <InlineLoader />
        </Col>
      </Row>
    )
  } else if (UboCount < 1) {
    if (hasUbosFromProvider) {
      showAddUboButton = false
      content = <DataSourceWizard personRoleType={SECTION_ULTIMATE_BENEFICIAL_OWNERS} />
    } else {
      showAddUboButton = true
      content = (
        <>
          <Row justifyContent="center">
            <Col auto>
              <ImageContainer>
                <StyledImg src="/assets/images/no-results-dynamic-entries.png" alt="no-results" />
              </ImageContainer>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col auto>
              <StyledParagraph textAlign="center">
                {t('ubo-summary-page.no-results.message')}
              </StyledParagraph>
            </Col>
          </Row>
        </>
      )
    }
  } else {
    showAddUboButton = true
    content = (
      <Row mb={40}>
        <Col>
          <DynamicEntriesTable
            dataList={uboSummary || []}
            headlines={tableHeadlines}
            transPrefix="ubo-summary-page"
          />
        </Col>
      </Row>
    )
  }

  return (
    <>
      <FormContentWrapper>
        {pageIsVerified && (
          <SuccessAlert>{t('ultimate-beneficial-owner-page.fully-verified-alert')}</SuccessAlert>
        )}

        <SummaryPageHeader
          ctaText={t(`sub-section-menu.${SECTION_ULTIMATE_BENEFICIAL_OWNERS}.create`)}
          onCreate={createUbo}
          isDisabled={isReadonly}
          hideCta={!showAddUboButton}
        />
        {content}
      </FormContentWrapper>

      <SummaryPageFooter sectionName={SECTION_ULTIMATE_BENEFICIAL_OWNERS} />
    </>
  )
}

export default BeneficialOwnerSummaryPage
